import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getPallet, issuePallet, getFIFOSuggestion, getChallanPDF } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Truck, Package, AlertTriangle, Clock, FileText } from 'lucide-react';
import { CATEGORY_COLORS, STATUS_COLORS } from '@/lib/constants';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function IssuePage() {
  const { palletId } = useParams();
  const navigate = useNavigate();
  const [pallet, setPallet] = useState(null);
  const [issueType, setIssueType] = useState('full');
  const [issuedTo, setIssuedTo] = useState('');
  const [poRef, setPoRef] = useState('');
  const [notes, setNotes] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [partialItems, setPartialItems] = useState([]);
  const [selectedBoxes, setSelectedBoxes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fifoSuggestion, setFifoSuggestion] = useState(null);

  useEffect(() => {
    loadPallet();
  }, [palletId]);

  const loadPallet = async () => {
    try {
      const res = await getPallet(palletId);
      setPallet(res.data);
      if (res.data.items?.length > 0) {
        // Check FIFO suggestion
        const itemCode = res.data.items[0].item_code;
        try {
          const fifo = await getFIFOSuggestion(itemCode);
          if (fifo.data.length > 1) {
            setFifoSuggestion(fifo.data);
          }
        } catch (err) { /* FIFO suggestion is optional */ }
      }
    } catch (err) {
      toast.error('Pallet not found');
      navigate('/inventory');
    }
  };

  const handleIssue = async () => {
    if (!issuedTo.trim()) return toast.error('Enter issued to');
    setLoading(true);
    try {
      const data = {
        issue_type: issueType,
        issued_to: issuedTo,
        po_ref: poRef,
        notes,
        is_internal: isInternal,
      };
      if (issueType === 'partial') {
        data.items = partialItems.filter(pi => (parseInt(pi.qty_pcs) || 0) > 0).map(pi => ({
          item_id: pi.item_id,
          qty_pcs: parseInt(pi.qty_pcs) || 0,
          weight_net_kg: parseFloat(pi.weight_net_kg) || 0,
          weight_gross_kg: parseFloat(pi.weight_gross_kg) || 0,
        }));
      }
      if (issueType === 'box') {
        data.box_ids = selectedBoxes;
      }

      const res = await issuePallet(pallet.id, data);
      if (res.data.challan_ref && !isInternal) {
        toast.success(`Issued - Challan: ${res.data.challan_ref}`);
        // Auto-download challan PDF
        try {
          const pdfRes = await getChallanPDF(res.data.challan_ref);
          if (pdfRes.data.url) {
            window.open(`${API_BASE}${pdfRes.data.url}`, '_blank');
          }
        } catch (err) { /* Challan PDF is optional — issue succeeded */ }
      } else {
        toast.success('Issued successfully');
      }
      navigate(`/pallet/${pallet.pallet_ref}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Issue failed');
    } finally {
      setLoading(false);
    }
  };

  if (!pallet) return <div className="flex justify-center py-20"><div className="animate-spin h-8 w-8 border-4 border-[#1a2fce] border-t-transparent rounded-full" /></div>;

  return (
    <div className="space-y-4 max-w-2xl mx-auto" data-testid="issue-page">
      <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
        <Truck size={20} /> Issue / Dispatch
      </h1>

      {/* FIFO Suggestion */}
      {fifoSuggestion && fifoSuggestion[0]?.pallet_ref !== pallet.pallet_ref && (
        <Card className="border-0 shadow-sm border-l-4 border-l-amber-500" data-testid="fifo-suggestion">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 text-sm">
              <Clock size={14} className="text-amber-500" />
              <span className="font-medium text-amber-700">FIFO Suggestion:</span>
              <span className="text-gray-600">{fifoSuggestion[0].pallet_ref} was placed first ({new Date(fifoSuggestion[0].date_placed).toLocaleDateString()})</span>
              <Button variant="link" className="text-xs p-0 h-auto text-[#1a2fce]" onClick={() => navigate(`/pallet/${fifoSuggestion[0].pallet_ref}`)}>View</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pallet Summary */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <Package size={20} className="text-[#1a2fce]" />
            <div>
              <div className="font-mono font-bold text-lg">{pallet.pallet_ref}</div>
              <div className="text-xs text-gray-500">{pallet.slot_address} · {pallet.items?.reduce((s, i) => s + (i.qty_pcs_total || 0), 0).toLocaleString()} pcs</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Issue Type */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4 space-y-3">
          <div className="flex gap-2">
            {['full', 'partial', 'box'].map(t => (
              <Button key={t} variant={issueType === t ? 'default' : 'outline'} className="flex-1 h-9 text-xs capitalize"
                style={issueType === t ? { backgroundColor: '#1a2fce' } : {}} onClick={() => setIssueType(t)}
                data-testid={`issue-type-${t}`}>
                {t === 'full' ? 'Full Issue' : t === 'partial' ? 'Partial Issue' : 'Box Issue'}
              </Button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <Checkbox checked={isInternal} onCheckedChange={setIsInternal} id="internal" />
            <Label htmlFor="internal" className="text-xs">Internal (Production floor, no challan)</Label>
          </div>

          <div className="space-y-2">
            <Label className="text-xs">Issued To *</Label>
            <Input value={issuedTo} onChange={e => setIssuedTo(e.target.value)} placeholder="Customer / Department" className="h-9 text-sm" data-testid="issued-to" />
          </div>
          {!isInternal && (
            <div><Label className="text-xs">PO Ref</Label>
            <Input value={poRef} onChange={e => setPoRef(e.target.value)} placeholder="Purchase order reference" className="h-9 text-sm" /></div>
          )}
          <div><Label className="text-xs">Notes</Label>
          <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional notes" className="h-9 text-sm" /></div>

          {/* Partial Issue Items */}
          {issueType === 'partial' && pallet.items?.map((item, i) => {
            const pi = partialItems.find(p => p.item_id === item.id) || { item_id: item.id, qty_pcs: '', weight_net_kg: '', weight_gross_kg: '', _manualNet: false, _manualGross: false };
            const unitNetWt = item.qty_pcs_total > 0 ? item.qty_weight_net_kg / item.qty_pcs_total : 0;
            const unitGrossWt = item.qty_pcs_total > 0 ? (item.qty_weight_gross_kg || 0) / item.qty_pcs_total : 0;
            const enteredQty = parseInt(pi.qty_pcs) || 0;
            const autoNet = enteredQty > 0 ? Math.round(enteredQty * unitNetWt * 100) / 100 : '';
            const autoGross = enteredQty > 0 ? Math.round(enteredQty * unitGrossWt * 100) / 100 : '';
            const displayNet = pi._manualNet ? pi.weight_net_kg : (pi.weight_net_kg !== '' && pi.weight_net_kg !== autoNet ? pi.weight_net_kg : autoNet);
            const displayGross = pi._manualGross ? pi.weight_gross_kg : (pi.weight_gross_kg !== '' && pi.weight_gross_kg !== autoGross ? pi.weight_gross_kg : autoGross);
            const isNetManual = pi._manualNet;
            const isGrossManual = pi._manualGross;

            return (
              <div key={i} className="p-3 border rounded-lg space-y-2" data-testid={`partial-item-${i}`}>
                <div className="text-sm font-medium">{item.description}</div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-xs">Qty (pcs)</Label>
                    <div className="text-[10px] text-gray-400">Avail: {item.qty_pcs_total}</div>
                    <Input type="number" className="h-9 text-sm" placeholder="0" value={pi.qty_pcs}
                      data-testid={`partial-qty-${i}`}
                      onChange={e => {
                        const newQty = e.target.value;
                        const q = parseInt(newQty) || 0;
                        const calcNet = q > 0 ? Math.round(q * unitNetWt * 100) / 100 : '';
                        const calcGross = q > 0 ? Math.round(q * unitGrossWt * 100) / 100 : '';
                        setPartialItems(prev => [
                          ...prev.filter(p => p.item_id !== item.id),
                          { item_id: item.id, qty_pcs: newQty, weight_net_kg: calcNet, weight_gross_kg: calcGross, _manualNet: false, _manualGross: false }
                        ]);
                      }} />
                  </div>
                  <div>
                    <Label className="text-xs">Net Wt (kg)</Label>
                    <div className="text-[10px] text-gray-400">Avail: {item.qty_weight_net_kg}</div>
                    <Input type="number" step="0.01" className={`h-9 text-sm ${isNetManual ? 'border-amber-400 bg-amber-50' : ''}`} placeholder="Auto"
                      value={displayNet}
                      data-testid={`partial-net-${i}`}
                      onChange={e => {
                        setPartialItems(prev => [
                          ...prev.filter(p => p.item_id !== item.id),
                          { ...pi, weight_net_kg: e.target.value, _manualNet: true }
                        ]);
                      }} />
                  </div>
                  <div>
                    <Label className="text-xs">Gross Wt (kg)</Label>
                    <div className="text-[10px] text-gray-400">Avail: {item.qty_weight_gross_kg || '\u2014'}</div>
                    <Input type="number" step="0.01" className={`h-9 text-sm ${isGrossManual ? 'border-amber-400 bg-amber-50' : ''}`} placeholder="Auto"
                      value={displayGross}
                      data-testid={`partial-gross-${i}`}
                      onChange={e => {
                        setPartialItems(prev => [
                          ...prev.filter(p => p.item_id !== item.id),
                          { ...pi, weight_gross_kg: e.target.value, _manualGross: true }
                        ]);
                      }} />
                  </div>
                </div>
                {/* FIX 5: Unit weight hint — visible, with formula */}
                {unitNetWt > 0 && enteredQty > 0 && !isNetManual && !isGrossManual && (
                  <div className="text-gray-500 space-y-0.5" style={{ fontSize: '12px', color: '#6B7280' }} data-testid={`unit-weight-hint-${i}`}>
                    <div>Calculated from unit weight: {unitNetWt.toFixed(5)} kg/pc</div>
                    <div>{enteredQty} pcs × {unitNetWt.toFixed(5)} kg/pc = {autoNet} kg (net)</div>
                    {unitGrossWt > 0 && <div>{enteredQty} pcs × {unitGrossWt.toFixed(5)} kg/pc = {autoGross} kg (gross)</div>}
                  </div>
                )}
                {(isNetManual || isGrossManual) && enteredQty > 0 && (
                  <div style={{ fontSize: '12px', color: '#d97706' }} data-testid={`manual-override-hint-${i}`}>
                    Manual override — auto-calculation disabled
                  </div>
                )}
              </div>
            );
          })}

          {/* Box Issue */}
          {issueType === 'box' && pallet.boxes?.length > 0 && (
            <div className="space-y-2">
              <div className="text-xs font-medium text-gray-500">Select boxes to issue:</div>
              {pallet.boxes?.filter(b => b.status !== 'issued').map((box, i) => (
                <div key={i} className="flex items-center gap-2 p-2 border rounded-lg">
                  <Checkbox checked={selectedBoxes.includes(box.id)} onCheckedChange={checked => {
                    if (checked) setSelectedBoxes([...selectedBoxes, box.id]);
                    else setSelectedBoxes(selectedBoxes.filter(id => id !== box.id));
                  }} data-testid={`box-check-${box.box_ref}`} />
                  <div className="flex-1">
                    <span className="font-mono text-sm">{box.box_ref}</span>
                    {box.is_exception && <Badge variant="outline" className="ml-1 text-[9px] text-amber-600 border-amber-300">Exception</Badge>}
                    <span className="text-xs text-gray-500 ml-2">{box.qty_pcs} pcs · {box.weight_net_kg} kg net · {box.weight_gross_kg} kg gross</span>
                  </div>
                  <Badge variant="outline" className="text-[9px]">{box.status}</Badge>
                </div>
              ))}
              {selectedBoxes.length > 0 && (() => {
                const selBoxes = pallet.boxes?.filter(b => selectedBoxes.includes(b.id)) || [];
                const totQty = selBoxes.reduce((s, b) => s + (b.qty_pcs || 0), 0);
                const totNet = selBoxes.reduce((s, b) => s + (b.weight_net_kg || 0), 0);
                const totGross = selBoxes.reduce((s, b) => s + (b.weight_gross_kg || 0), 0);
                return (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="box-issue-summary">
                    <div className="text-xs font-semibold text-blue-800 mb-1">Selected: {selectedBoxes.length} box(es)</div>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div><div className="text-lg font-bold text-blue-900">{totQty.toLocaleString()}</div><div className="text-[10px] text-blue-600">Total Qty</div></div>
                      <div><div className="text-lg font-bold text-blue-900">{totNet.toFixed(2)}</div><div className="text-[10px] text-blue-600">Net Wt (kg)</div></div>
                      <div><div className="text-lg font-bold text-blue-900">{totGross.toFixed(2)}</div><div className="text-[10px] text-blue-600">Gross Wt (kg)</div></div>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
          {issueType === 'box' && (!pallet.boxes || pallet.boxes.filter(b => b.status !== 'issued').length === 0) && (
            <div className="text-center py-4 text-gray-400 text-sm">No boxes available to issue</div>
          )}

          <Button onClick={handleIssue} disabled={loading} className="w-full h-11 text-sm font-semibold"
            style={{ backgroundColor: '#d97706' }} data-testid="confirm-issue">
            <Truck size={14} className="mr-1" /> {loading ? 'Processing...' : isInternal ? 'Confirm Internal Issue' : 'Confirm & Generate Challan'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
